/**
 * Shared JSON-LD for Hive Index templates.
 * Canonical site origin must match seo.jsx / gatsby siteMetadata.siteUrl usage.
 */

import { average } from "./numbers"
import { topicIsoDate } from "./topics"

const HIVE_SITE = "https://thehiveindex.com"
const HIVE_DATA_LICENSE_URL = `${HIVE_SITE}/data-license/`
const HIVE_ORG_ID = `${HIVE_SITE}/#organization`
const HIVE_WEBSITE_ID = `${HIVE_SITE}/#website`
/** Served from `static/icon.png` (root URL, no content hash). */
const HIVE_LOGO_URL = `${HIVE_SITE}/icon.png`
const HIVE_ORG_SAME_AS = [
  "https://twitter.com/thehiveindex",
  "https://www.reddit.com/r/hiveindex/",
]

const hiveOrganizationNode = () => ({
  "@type": "Organization",
  "@id": HIVE_ORG_ID,
  name: "Hive Index",
  url: `${HIVE_SITE}/`,
})

const hiveOrgRef = () => ({ "@id": HIVE_ORG_ID })

const webSitePartial = () => ({
  "@type": "WebSite",
  name: "Hive Index",
  url: `${HIVE_SITE}/`,
})

/** External join/home URLs: keep query when present (e.g. Slack/Discord), strip hash fragment only. */
function schemaPrimaryExternalUrl(url) {
  if (url == null || url === "") return ""
  const s = String(url).trim()
  if (!s) return ""
  const base = s.split("#")[0]
  return /^https?:\/\//i.test(base) ? base.trim() : s.trim()
}

function listItemForCommunity(community, position) {
  const listItem = {
    "@type": "ListItem",
    position,
    name: community.name,
    url: `${HIVE_SITE}/communities/${community.slug}/`,
  }

  if (community.logo) {
    listItem.image = {
      "@type": "ImageObject",
      url: community.logo,
      contentUrl: community.logo,
    }
  }

  return listItem
}

/** Carousel-friendly ItemList wrapper: include numberOfItems so Google sees the true list size, not just the slice. */
function buildItemList({ name, description, items, totalCount }) {
  const list = {
    "@type": "ItemList",
    name,
    numberOfItems: totalCount != null ? totalCount : items.length,
    itemListElement: items,
  }
  if (description) list.description = description
  return list
}

function faqAnswerPlain(item) {
  if (item.answerPlain) return item.answerPlain
  if (typeof item.answer === "string") return item.answer
  return ""
}

/** FAQPage node for @graph when a listing page has an on-page FAQ band. */
function buildFaqPageGraphNode(faq, pageUrl) {
  if (!faq?.length) return null

  const mainEntity = faq
    .map(item => {
      const text = faqAnswerPlain(item)
      if (!text) return null
      return {
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text,
        },
      }
    })
    .filter(Boolean)

  if (!mainEntity.length) return null

  return {
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    mainEntity,
  }
}

/** Build a BreadcrumbList from `[{ name, url }]`; the final crumb omits `url` since it's the current page. */
function buildBreadcrumb(crumbs) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => {
      const isLast = i === crumbs.length - 1
      const node = {
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
      }
      if (c.url && !isLast) node.item = c.url
      return node
    }),
  }
}

/** Platform directory page: Hive org, vendor org, CollectionPage + ItemLists. */
function getSchemaForPlatform({
  platform,
  communities,
  topicCounts,
  title,
  description,
  faq,
}) {
  const slug = platform.slug
  const platformPageUrl = `${HIVE_SITE}/platforms/${slug}/`
  const linkRaw =
    platform.link != null && String(platform.link).trim() !== ""
      ? String(platform.link)
          .split("?")[0]
          .trim()
      : null

  const platformVendorNode = linkRaw
    ? {
        "@type": "Organization",
        "@id": `${platformPageUrl}#platform`,
        name: platform.name,
        url: linkRaw,
        ...(platform.logoSmall ? { logo: platform.logoSmall } : {}),
      }
    : {
        "@type": "Organization",
        "@id": `${platformPageUrl}#platform`,
        name: platform.name,
        ...(platform.logoSmall ? { logo: platform.logoSmall } : {}),
      }

  const communitiesItems = communities
    .slice(0, 20)
    .map((c, index) => listItemForCommunity(c, index + 1))

  const topicEntries = Object.keys(topicCounts)
    .map(key => topicCounts[key])
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))
    .filter(entry => entry.topic)

  const topicItems = topicEntries.map((entry, index) => {
    const topicNode = entry.topic
    const listItem = {
      "@type": "ListItem",
      position: index + 1,
      name: topicNode.name,
      url: `${HIVE_SITE}/topics/${topicNode.slug}/platform/${slug}/`,
    }
    if (topicNode.image) {
      listItem.image = {
        "@type": "ImageObject",
        url: topicNode.image,
        contentUrl: topicNode.image,
      }
    }
    return listItem
  })

  const graph = [
    hiveOrganizationNode(),
    platformVendorNode,
    {
      "@type": "CollectionPage",
      "@id": platformPageUrl,
      url: platformPageUrl,
      name: title,
      ...(description ? { description } : {}),
      about: { "@id": `${platformPageUrl}#platform` },
      isPartOf: webSitePartial(),
      provider: hiveOrgRef(),
      mainEntity: buildItemList({
        name: `${platform.name} Communities`,
        items: communitiesItems,
        totalCount: communities.length,
      }),
      hasPart: [
        buildItemList({
          name: `Topics Discussed in ${platform.name} Communities`,
          items: topicItems,
          totalCount: topicEntries.length,
        }),
      ],
    },
    buildBreadcrumb([
      { name: "Home", url: `${HIVE_SITE}/` },
      { name: "Platforms", url: `${HIVE_SITE}/platforms/` },
      { name: platform.name, url: platformPageUrl },
    ]),
  ]

  const faqNode = buildFaqPageGraphNode(faq, platformPageUrl)
  if (faqNode) graph.push(faqNode)

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

/** Topic listing page CollectionPage (+ Hive org reference in @graph). */
function getSchemaForTopic({
  topic,
  description,
  createdDate,
  updatedDate,
  filteredCommunities,
  faq,
}) {
  const pageUrl = `${HIVE_SITE}/topics/${topic.slug}/`
  const catalogName = `${topic.name} Communities`

  const graph = [
    hiveOrganizationNode(),
    {
      "@type": "CollectionPage",
      "@id": pageUrl,
      url: pageUrl,
      name: catalogName,
      ...(createdDate ? { datePublished: createdDate } : {}),
      ...(updatedDate ? { dateModified: updatedDate } : {}),
      ...(description ? { description } : {}),
      isPartOf: webSitePartial(),
      provider: hiveOrgRef(),
      mainEntity: buildItemList({
        name: catalogName,
        description,
        items: filteredCommunities
          .slice(0, 20)
          .map((c, index) => listItemForCommunity(c, index + 1)),
        totalCount: filteredCommunities.length,
      }),
    },
    buildBreadcrumb([
      { name: "Home", url: `${HIVE_SITE}/` },
      { name: "Topics", url: `${HIVE_SITE}/topics/` },
      { name: topic.name, url: pageUrl },
    ]),
  ]

  const faqNode = buildFaqPageGraphNode(faq, pageUrl)
  if (faqNode) graph.push(faqNode)

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

/** Feature + topic facet listing page (/topics/{topic}/feature/{feature}/). */
function getSchemaForFeatureTopic({
  topic,
  feature,
  seoTitle,
  description,
  createdDate,
  updatedDate,
  filteredCommunities,
  faq,
}) {
  const pageUrl = `${HIVE_SITE}/topics/${topic.slug}/feature/${feature.slug}/`

  const graph = [
    hiveOrganizationNode(),
    {
      "@type": "CollectionPage",
      "@id": pageUrl,
      url: pageUrl,
      name: seoTitle,
      ...(createdDate ? { datePublished: createdDate } : {}),
      ...(updatedDate ? { dateModified: updatedDate } : {}),
      ...(description ? { description } : {}),
      isPartOf: webSitePartial(),
      provider: hiveOrgRef(),
      mainEntity: buildItemList({
        name: seoTitle,
        description,
        items: filteredCommunities
          .slice(0, 20)
          .map((c, index) => listItemForCommunity(c, index + 1)),
        totalCount: filteredCommunities.length,
      }),
    },
    buildBreadcrumb([
      { name: "Home", url: `${HIVE_SITE}/` },
      { name: "Topics", url: `${HIVE_SITE}/topics/` },
      { name: topic.name, url: `${HIVE_SITE}/topics/${topic.slug}/` },
      { name: feature.name, url: pageUrl },
    ]),
  ]

  const faqNode = buildFaqPageGraphNode(faq, pageUrl)
  if (faqNode) graph.push(faqNode)

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

/** Topic + platform facet listing page. */
function getSchemaForPlatformTopic({
  topic,
  platform,
  seoTitle,
  description,
  createdDate,
  updatedDate,
  filteredCommunities,
  faq,
}) {
  const pageUrl = `${HIVE_SITE}/topics/${topic.slug}/platform/${platform.slug}/`

  const graph = [
    hiveOrganizationNode(),
    {
      "@type": "CollectionPage",
      "@id": pageUrl,
      url: pageUrl,
      name: seoTitle,
      ...(createdDate ? { datePublished: createdDate } : {}),
      ...(updatedDate ? { dateModified: updatedDate } : {}),
      ...(description ? { description } : {}),
      isPartOf: webSitePartial(),
      provider: hiveOrgRef(),
      mainEntity: buildItemList({
        name: seoTitle,
        description,
        items: filteredCommunities
          .slice(0, 20)
          .map((c, index) => listItemForCommunity(c, index + 1)),
        totalCount: filteredCommunities.length,
      }),
    },
    buildBreadcrumb([
      { name: "Home", url: `${HIVE_SITE}/` },
      { name: "Topics", url: `${HIVE_SITE}/topics/` },
      { name: topic.name, url: `${HIVE_SITE}/topics/${topic.slug}/` },
      { name: platform.name, url: pageUrl },
    ]),
  ]

  const faqNode = buildFaqPageGraphNode(faq, pageUrl)
  if (faqNode) graph.push(faqNode)

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

/** Category browse page: CollectionPage whose mainEntity is the list of topic links in the category. */
function getSchemaForCategory({
  slug,
  name,
  description,
  categoryTopicsNodes,
}) {
  const pageUrl = `${HIVE_SITE}/category/${slug}/`

  const topicItems = categoryTopicsNodes.map((t, index) => {
    const listItem = {
      "@type": "ListItem",
      position: index + 1,
      name: `${t.name} Communities`,
      url: `${HIVE_SITE}/topics/${t.slug}/`,
    }
    if (t.image) {
      listItem.image = {
        "@type": "ImageObject",
        url: t.image,
        contentUrl: t.image,
      }
    }
    return listItem
  })

  return {
    "@context": "https://schema.org",
    "@graph": [
      hiveOrganizationNode(),
      {
        "@type": "CollectionPage",
        "@id": pageUrl,
        url: pageUrl,
        name: `${name} Communities`,
        ...(description ? { description } : {}),
        isPartOf: webSitePartial(),
        provider: hiveOrgRef(),
        mainEntity: buildItemList({
          name: `${name} Topics`,
          items: topicItems,
          totalCount: categoryTopicsNodes.length,
        }),
      },
      buildBreadcrumb([
        { name: "Home", url: `${HIVE_SITE}/` },
        { name: "Topics", url: `${HIVE_SITE}/topics/` },
        { name, url: pageUrl },
      ]),
    ],
  }
}

/**
 * Homepage: Organization + WebSite + CollectionPage.
 * FAQ sections on the site use plain semantic HTML (<dl>/<dt>/<dd>). We do not
 * emit FAQPage JSON-LD here: Google's FAQ rich result for general sites is gone
 * (May 2026), and declaring the homepage as FAQPage misstates what the page is.
 * @see https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */
function getSchemaForHomepage() {
  const homeUrl = `${HIVE_SITE}/`

  const graph = [
    {
      "@type": "Organization",
      "@id": HIVE_ORG_ID,
      name: "Hive Index",
      url: homeUrl,
      logo: { "@type": "ImageObject", url: HIVE_LOGO_URL },
      sameAs: HIVE_ORG_SAME_AS,
    },
    {
      "@type": "WebSite",
      "@id": HIVE_WEBSITE_ID,
      name: "Hive Index",
      url: homeUrl,
      publisher: { "@id": HIVE_ORG_ID },
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${HIVE_SITE}/search/?keyword={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "CollectionPage",
      "@id": `${homeUrl}#page`,
      url: homeUrl,
      name: "Hive Index — The #1 Directory of Online Communities",
      description:
        "Browse 4,000+ online communities across Discord, Slack, Reddit, Circle, and 30+ platforms. Organized by topic, platform, and community features. Free.",
      isPartOf: { "@id": HIVE_WEBSITE_ID },
      publisher: { "@id": HIVE_ORG_ID },
    },
  ]

  return { "@context": "https://schema.org", "@graph": graph }
}

/**
 * Community profile: WebPage (this URL) + Organization (canonical community URL where possible).
 * Hive listing URL stays on WebPage.url; Organization.url prefers external home when present.
 * On-page FAQs use semantic HTML only (no FAQPage JSON-LD: the listing is about the community,
 * not primarily an FAQ document).
 */
function getSchemaForCommunityListing({
  communitySlug,
  communityName,
  shortDescriptionPlain,
  generatedDescriptionPlusSource,
  outboundUrlRaw,
  logoUrl,
  reviews,
  breadcrumbListItems,
}) {
  const profilePageUrl = `${HIVE_SITE}/communities/${communitySlug}/`
  const communityThingId = `${profilePageUrl}#community`

  const fullDescription = generatedDescriptionPlusSource
    ? String(generatedDescriptionPlusSource).slice(0, 5000)
    : ""
  const shortDescription = shortDescriptionPlain
    ? String(shortDescriptionPlain).slice(0, 5000)
    : ""

  const externalPrimary = schemaPrimaryExternalUrl(outboundUrlRaw)

  const sameAs = externalPrimary ? [profilePageUrl] : undefined

  const organizationNode = {
    "@type": "Organization",
    "@id": communityThingId,
    name: communityName,
    ...(fullDescription ? { description: fullDescription } : {}),
    ...(externalPrimary ? { url: externalPrimary } : {}),
    ...(sameAs ? { sameAs } : {}),
    ...(logoUrl
      ? {
          logo: { "@type": "ImageObject", url: logoUrl },
          image: { "@type": "ImageObject", url: logoUrl },
        }
      : {}),
  }

  const visibleReviews = (reviews || []).filter(
    r => r.overall != null && r.overall !== ""
  )
  if (visibleReviews.length) {
    const ratingValue = average(visibleReviews.map(r => r.overall)).toFixed(1)
    organizationNode.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue,
      ratingCount: String(visibleReviews.length),
      bestRating: "5",
      worstRating: "1",
    }
    organizationNode.review = visibleReviews.map(r => {
      const datePublished = topicIsoDate(r.timestamp)
      return {
        "@type": "Review",
        ...(r.publicReview ? { reviewBody: r.publicReview } : {}),
        ...(datePublished ? { datePublished } : {}),
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(r.overall),
          bestRating: "5",
          worstRating: "1",
        },
        author: {
          "@type": "Person",
          name: r.yourFirstName ? r.yourFirstName : "Anonymous",
        },
      }
    })
  }

  const webpageNode = {
    "@type": "WebPage",
    "@id": `${profilePageUrl}#page`,
    url: profilePageUrl,
    name: communityName,
    ...(shortDescription ? { description: shortDescription } : {}),
    isPartOf: webSitePartial(),
    mainEntity: { "@id": communityThingId },
  }

  const graph = [webpageNode, organizationNode]
  if (breadcrumbListItems && breadcrumbListItems.length) {
    graph.push({
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbListItems,
    })
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

/** Topic statistics page: WebPage + Dataset with key metrics and FAQ. */
function getSchemaForTopicStats({
  topic,
  stats,
  report,
  updatedDate,
  description,
}) {
  const pageUrl = `${HIVE_SITE}/topics/${topic.slug}/stats/`
  const pageName = `${topic.name} Communities Report`

  const variableMeasured = [
    {
      "@type": "PropertyValue",
      name: "Total communities",
      value: String(stats.total),
    },
    {
      "@type": "PropertyValue",
      name: "Platforms used",
      value: String(stats.platformCount || 0),
    },
  ]

  if (stats.averageMembers) {
    variableMeasured.push({
      "@type": "PropertyValue",
      name: "Average members",
      value: String(Math.round(stats.averageMembers)),
    })
  }

  if (stats.medianMembers) {
    variableMeasured.push({
      "@type": "PropertyValue",
      name: "Median members",
      value: String(Math.round(stats.medianMembers)),
    })
  }

  stats.platformBreakdown.slice(0, 5).forEach(item => {
    variableMeasured.push({
      "@type": "PropertyValue",
      name: `${item.name} share`,
      value: `${item.percent}%`,
    })
  })

  stats.accessBreakdown.forEach(item => {
    variableMeasured.push({
      "@type": "PropertyValue",
      name: item.label,
      value: `${item.percent}%`,
    })
  })

  const graph = [
    hiveOrganizationNode(),
    {
      "@type": "WebPage",
      "@id": pageUrl,
      url: pageUrl,
      name: pageName,
      ...(description ? { description } : {}),
      ...(updatedDate ? { dateModified: updatedDate } : {}),
      isPartOf: webSitePartial(),
      provider: hiveOrgRef(),
      about: {
        "@type": "Thing",
        name: `${topic.name} online communities`,
      },
      mainEntity: {
        "@type": "Dataset",
        name: pageName,
        description:
          description ||
          `Community statistics and trends for ${topic.name} on Hive Index.`,
        creator: hiveOrgRef(),
        publisher: hiveOrgRef(),
        url: pageUrl,
        license: HIVE_DATA_LICENSE_URL,
        isAccessibleForFree: true,
        ...(updatedDate ? { dateModified: updatedDate } : {}),
        variableMeasured,
        keywords: [
          topic.name,
          "online communities",
          "community platforms",
          "community statistics",
        ],
      },
    },
    buildBreadcrumb([
      { name: "Home", url: `${HIVE_SITE}/` },
      { name: "Topics", url: `${HIVE_SITE}/topics/` },
      { name: topic.name, url: `${HIVE_SITE}/topics/${topic.slug}/` },
      { name: "Report", url: pageUrl },
    ]),
  ]

  if (report && report.faq && report.faq.length) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: report.faq.map(item => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    })
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

/** Marketing or utility WebPage with optional on-page FAQ. */
function getSchemaForWebPageWithFaq({
  pageUrl,
  name,
  description,
  faq,
  breadcrumbs,
}) {
  const graph = [
    hiveOrganizationNode(),
    {
      "@type": "WebPage",
      "@id": pageUrl,
      url: pageUrl,
      name,
      ...(description ? { description } : {}),
      isPartOf: webSitePartial(),
      provider: hiveOrgRef(),
    },
  ]

  if (breadcrumbs?.length) {
    graph.push(buildBreadcrumb(breadcrumbs))
  }

  const faqNode = buildFaqPageGraphNode(faq, pageUrl)
  if (faqNode) graph.push(faqNode)

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

/** Directory index page (platforms, features) with ItemList + FAQ. */
function getSchemaForDirectoryIndex({
  pageUrl,
  name,
  description,
  faq,
  breadcrumbs,
  listName,
  listItems,
  totalCount,
}) {
  const graph = [
    hiveOrganizationNode(),
    {
      "@type": "CollectionPage",
      "@id": pageUrl,
      url: pageUrl,
      name,
      ...(description ? { description } : {}),
      isPartOf: webSitePartial(),
      provider: hiveOrgRef(),
      ...(listItems?.length
        ? {
            mainEntity: buildItemList({
              name: listName,
              items: listItems,
              totalCount: totalCount ?? listItems.length,
            }),
          }
        : {}),
    },
  ]

  if (breadcrumbs?.length) {
    graph.push(buildBreadcrumb(breadcrumbs))
  }

  const faqNode = buildFaqPageGraphNode(faq, pageUrl)
  if (faqNode) graph.push(faqNode)

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

/** Feature directory page (/features/{slug}/). */
function getSchemaForFeature({
  feature,
  description,
  filteredCommunities,
  faq,
}) {
  const pageUrl = `${HIVE_SITE}/features/${feature.slug}/`
  const catalogName = feature.name
    ? `Communities with ${feature.name}`
    : `Communities ${feature.sentenceEnding || ""}`.trim()

  const graph = [
    hiveOrganizationNode(),
    {
      "@type": "CollectionPage",
      "@id": pageUrl,
      url: pageUrl,
      name: catalogName,
      ...(description ? { description } : {}),
      isPartOf: webSitePartial(),
      provider: hiveOrgRef(),
      mainEntity: buildItemList({
        name: catalogName,
        description,
        items: filteredCommunities
          .slice(0, 20)
          .map((c, index) => listItemForCommunity(c, index + 1)),
        totalCount: filteredCommunities.length,
      }),
    },
    buildBreadcrumb([
      { name: "Home", url: `${HIVE_SITE}/` },
      { name: "Features", url: `${HIVE_SITE}/features/` },
      { name: feature.name || feature.slug, url: pageUrl },
    ]),
  ]

  const faqNode = buildFaqPageGraphNode(faq, pageUrl)
  if (faqNode) graph.push(faqNode)

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  }
}

export {
  HIVE_SITE,
  HIVE_DATA_LICENSE_URL,
  HIVE_ORG_ID,
  HIVE_WEBSITE_ID,
  HIVE_LOGO_URL,
  getSchemaForPlatform,
  getSchemaForTopic,
  getSchemaForPlatformTopic,
  getSchemaForFeatureTopic,
  getSchemaForCategory,
  getSchemaForHomepage,
  getSchemaForCommunityListing,
  getSchemaForTopicStats,
  getSchemaForWebPageWithFaq,
  getSchemaForDirectoryIndex,
  getSchemaForFeature,
}
